import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Plus, Minus, Search, X, ShieldCheck } from 'lucide-react';
import Pagination from '@repo/ui/components/Pagination';
import {
  useEntityAdmins,
  useEntityMembers,
  usePromoteToEntityAdmin,
  useDemoteToEntityMember,
  getEntityUserName,
  EntityType,
} from '../../hooks/users';
import { useIsMobile } from '../../hooks/useIsMobile';

interface EntityAdminMemberTableProps {
  entityId: string;
  entityType: EntityType;
  entityName?: string;
  onClearEntityFilter?: () => void;
}

export const EntityAdminMemberTable = ({
  entityId,
  entityType,
  entityName,
  onClearEntityFilter,
}: EntityAdminMemberTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [adminCurrentPage, setAdminCurrentPage] = useState(1);
  const [memberCurrentPage, setMemberCurrentPage] = useState(1);
  const handleSearchUpdate = (value: string) => {
    setSearchQuery(value);
    setAdminCurrentPage(1);
    setMemberCurrentPage(1);
  };

  const [allAdminIds, setAllAdminIds] = useState<Set<string>>(new Set());

  const {
    data: adminData,
    isLoading: isLoadingAdmins,
    isPending: isPendingAdmins,
  } = useEntityAdmins({
    entityId,
    entityType,
    searchQuery,
    page: adminCurrentPage,
  });

  const { data: allAdminData } = useEntityAdmins({
    entityId,
    entityType,
    searchQuery: '',
    page: 1,
    limit: 1000,
  });

  useEffect(() => {
    if (allAdminData?.admins) {
      setAllAdminIds(new Set(allAdminData.admins.map(admin => admin.registrantid)));
    }
  }, [allAdminData]);

  const {
    data: memberData,
    isLoading: isLoadingMembers,
    isPending: isPendingMembers,
  } = useEntityMembers({
    entityId,
    entityType,
    searchQuery,
    page: memberCurrentPage,
  });

  const promoteToAdmin = usePromoteToEntityAdmin(entityId, entityType);
  const demoteToMember = useDemoteToEntityMember(entityId, entityType);

  const handlePromote = (registrantId: string) => {
    promoteToAdmin.mutate(registrantId);
  };

  const handleDemote = (registrantId: string) => {
    demoteToMember.mutate(registrantId);
  };

  const getEntityTypeName = (type: EntityType): string => {
    switch (type) {
      case 'location':
        return 'Location';
      case 'unit':
        return 'Unit';
      case 'contract':
        return 'Contract';
      default:
        return 'Entity';
    }
  };

  const title = entityName ? entityName : `${getEntityTypeName(entityType)} Users`;

  const isMobile = useIsMobile();
  const isInitialLoading = isPendingAdmins && isPendingMembers;

  if (isInitialLoading) {
    return (
      <div className="flex h-40 w-full items-center justify-center rounded-lg bg-[var(--anl-surface)] shadow-md">
        <div className="text-[var(--anl-text-muted)]">Loading {entityType} data...</div>
      </div>
    );
  }

  const admins = adminData?.admins || [];
  const members = memberData?.members || [];

  const adminIds = allAdminIds;

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const buttonVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.1 },
    tap: { scale: 0.95 },
  };

  return (
    <motion.div
      className="overflow-hidden rounded-lg bg-[var(--anl-surface)] shadow-md"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div
        className={`flex text-white ${isMobile ? 'flex-col gap-2 p-3' : 'flex-row items-center justify-between px-3 py-2.5'}`}
        style={{ backgroundColor: '#253870' }}
      >
        <div className="relative flex flex-1 items-center rounded-md bg-white/10 px-3 py-1.5">
          <Search size={15} className="mr-2 flex-shrink-0 text-white/60" />
          <input
            type="text"
            placeholder="Search users..."
            className="w-full border-none bg-transparent text-sm text-white placeholder-white/50 outline-none"
            value={searchQuery}
            onChange={e => handleSearchUpdate(e.target.value)}
          />
          {searchQuery && (
            <button
              className="text-white/60 hover:text-white"
              onClick={() => handleSearchUpdate('')}
            >
              <X size={13} />
            </button>
          )}
        </div>
        <div className={`flex items-center justify-between flex-shrink-0 ${isMobile ? '' : 'pl-3 text-right'}`}>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight">{title}</p>
            {entityName && (
              <p className="text-xs text-white/70">{getEntityTypeName(entityType)} Filter</p>
            )}
          </div>
          {onClearEntityFilter && (
            <button
              onClick={onClearEntityFilter}
              className="ml-2 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              title={`Clear ${entityType} filter`}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'}`}>
        {/* Admins column */}
        <div className={`border-[var(--anl-border)] ${isMobile ? 'w-full border-b' : 'w-1/2 border-r'}`}>
          <div className="border-b border-[var(--anl-border)]">
            <div className="flex items-center bg-[var(--anl-surface-elev)] px-6 py-3">
              <div className="flex items-center gap-2">
                <span
                  className="flex h-5 w-5 items-center justify-center rounded-full"
                  style={{ backgroundColor: 'var(--anl-accent)' }}
                >
                  <ShieldCheck className="h-3 w-3 text-white" />
                </span>
                <div className="text-sm font-medium text-[var(--anl-text)]">Admins</div>
              </div>
            </div>
          </div>
          <div className={`overflow-y-auto ${isMobile ? 'h-[220px]' : 'h-[400px]'}`}>
            {isLoadingAdmins ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-[var(--anl-text-muted)]">Loading admins...</div>
              </div>
            ) : admins.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-[var(--anl-text-muted)]">No admins found</div>
              </div>
            ) : (
              <motion.div
                className="divide-y divide-[var(--anl-border)]"
                variants={containerVariants}
                initial="hidden"
                animate="show"
              >
                <AnimatePresence>
                  {admins.map(admin => (
                    <motion.div
                      key={admin.registrantid}
                      className={`flex items-center justify-between hover:bg-[var(--anl-surface-elev)] ${isMobile ? 'px-4 py-3' : 'px-6 py-4'}`}
                      variants={itemVariants}
                      layout
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                          style={{ backgroundColor: 'var(--anl-accent)' }}
                        >
                          <ShieldCheck className="h-3 w-3 text-white" />
                        </span>
                        <div className="truncate text-sm font-medium text-[var(--anl-text)]">
                          {getEntityUserName(admin)}
                        </div>
                      </div>
                      <motion.button
                        title="Demote to Member"
                        className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--anl-text-muted)] transition-colors hover:bg-[var(--anl-border)]"
                        onClick={() => handleDemote(admin.registrantid)}
                        variants={buttonVariants}
                        whileHover="hover"
                        whileTap="tap"
                      >
                        <Minus size={16} />
                      </motion.button>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
          <div className="border-t border-[var(--anl-border)]">
            <Pagination
              totalPages={adminData?.totalPages || 1}
              currentPage={adminCurrentPage}
              onPageChange={setAdminCurrentPage}
              className="py-2"
              variant="dark"
            />
          </div>
        </div>

        {/* Members column */}
        <div className={isMobile ? 'w-full' : 'w-1/2'}>
          <div className="border-b border-[var(--anl-border)]">
            <div className="flex items-center bg-[var(--anl-surface-elev)] px-6 py-3">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-[var(--anl-text-muted)]" />
                <div className="text-sm font-medium text-[var(--anl-text)]">Members</div>
              </div>
            </div>
          </div>
          <div className={`overflow-y-auto ${isMobile ? 'h-[220px]' : 'h-[400px]'}`}>
            {isLoadingMembers ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-[var(--anl-text-muted)]">Loading members...</div>
              </div>
            ) : members.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="text-[var(--anl-text-muted)]">No members found</div>
              </div>
            ) : (
              <motion.div
                className="divide-y divide-[var(--anl-border)]"
                variants={containerVariants}
                initial="hidden"
                animate="show"
              >
                <AnimatePresence>
                  {members.map(member => (
                    <motion.div
                      key={member.registrantid}
                      className={`flex items-center justify-between hover:bg-[var(--anl-surface-elev)] ${isMobile ? 'px-4 py-3' : 'px-6 py-4'}`}
                      variants={itemVariants}
                      layout
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 flex-shrink-0 text-[var(--anl-text-muted)]" />
                        <div className="truncate text-sm font-medium text-[var(--anl-text)]">
                          {getEntityUserName(member)}
                        </div>
                      </div>
                      {!adminIds.has(member.registrantid) && (
                        <motion.button
                          title="Promote to Admin"
                          className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--anl-text-muted)] transition-colors hover:bg-[var(--anl-border)] hover:text-[var(--anl-text)]"
                          onClick={() => handlePromote(member.registrantid)}
                          variants={buttonVariants}
                          whileHover="hover"
                          whileTap="tap"
                        >
                          <Plus size={16} />
                        </motion.button>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
          <div className="border-t border-[var(--anl-border)]">
            <Pagination
              totalPages={memberData?.totalPages || 1}
              currentPage={memberCurrentPage}
              onPageChange={setMemberCurrentPage}
              className="py-2"
              variant="dark"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};
